/* eslint-disable max-lines */
import React, { useCallback, useEffect, useState, useMemo } from 'react'
import styled from 'styled-components'
import _ from 'underscore'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'

import FilterList from 'pages/Device/ThermostatRthPanel/GraphFilterModal/FilterList'
import LegendConfig from 'pages/Device/ThermostatRthPanel/Legend/legendItemsConfig'
import { getDefaultSelectedSeriesItems } from 'pages/Device/ThermostatRthPanel/Chart/plotHelper'

import Modal from 'components/Modal'

import { ButtonContainer } from 'styles/shared'
import { color } from 'styles/colors'

import { RuntimeHistory, legendZone } from 'types/RuntimeHistory'
import GA4 from 'helpers/ga4'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import { Alert } from 'types/Alert'

type Props = {
  rthData: RuntimeHistory | null
  deviceId: string
  zones:Array<Required<legendZone>>;
  alerts: Array<Alert>
  closeCallback: () => void
  onSaveGraphFilterCallback: (selectedLegends) => void
  showHoverData?: boolean
  isHehm?: boolean
}

const GraphFilterModal = ({
  rthData,
  deviceId,
  zones,
  alerts,
  closeCallback,
  onSaveGraphFilterCallback,
  showHoverData,
  isHehm = false
}: Props) => {
  const { defaultSelectedLegendItems, indoorUnitLegendItems, outdoorLegendItems, outdoorUnitLegendItems, displayGroups, isControlHub } = useMemo(() => {
    const selectedLegends = getDefaultSelectedSeriesItems(deviceId, rthData, isHehm)
    const { alerts = [], series = [], zoneSeries = [] } = selectedLegends
    const isControlHub = rthData ? rthData.isControlHub : false

    let indoorUnitLegendItems
    if (isHehm) {
      indoorUnitLegendItems = LegendConfig.HEHM_INDOOR_LEGEND_ITEMS
    } else if (isControlHub) {
      indoorUnitLegendItems = _.union(
        LegendConfig.INDOOR_UNIT_LEGEND_ITEMS,
        LegendConfig.CONTROL_HUB_INDOOR_UNIT_LEGEND_ITEMS
      )
    } else {
      indoorUnitLegendItems = LegendConfig.INDOOR_UNIT_LEGEND_ITEMS
    }

    const outdoorLegendItems = isHehm ? [] : LegendConfig.OUTDOOR_LEGEND_ITEMS
    const outdoorUnitLegendItems = isHehm ? LegendConfig.HEHM_OUTDOOR_LEGEND_ITEMS : LegendConfig.OUTDOOR_UNIT_LEGEND_ITEMS

    const displayGroups = {
      indoorUnit: indoorUnitLegendItems.some(item => !item.emptyDataSet),
      outdoor: outdoorLegendItems.some(item => !item.emptyDataSet),
      outdoorUnit: outdoorUnitLegendItems.some(item => !item.emptyDataSet)
    }

    const allLegends = [
      ...(displayGroups.indoorUnit ? indoorUnitLegendItems : []),
      ...(displayGroups.outdoor ? outdoorLegendItems : []),
      ...(displayGroups.outdoorUnit ? outdoorUnitLegendItems : [])
    ]
    const filteredSeries = allLegends.filter(item => series.includes(item.seriesName)).map(item => item.seriesName)
    return {
      defaultSelectedLegendItems: {
        alerts,
        series: filteredSeries,
        zoneSeries,
        showHoverData
      },
      isControlHub,
      displayGroups,
      indoorUnitLegendItems,
      outdoorLegendItems,
      outdoorUnitLegendItems
    }
  },
  [deviceId, rthData, showHoverData, isHehm])

  const [displayTooltip, setDisplayTooltip] = useState(showHoverData)
  const [selectedLegends, setSelectedLegends] = useState({
    ...defaultSelectedLegendItems
  })

  const [selectedDataPoints, setSelectedDataPoints] = useState(0)

  const [isSelectionLimitExceeds, setIsSelectionLimitExceeds] = useState(false)
  const indoorLegendItems =
    zones.length > 1
      ? LegendConfig.INDOOR_ZONE_LEGEND_ITEMS
      : LegendConfig.INDOOR_LEGEND_ITEMS

  useEffect(() => {
    let legendItemsLength = selectedLegends.series
      ? selectedLegends.series?.length
      : 0
    selectedLegends.zoneSeries?.map((zoneSeries) => {
      legendItemsLength += zoneSeries.series ? zoneSeries.series?.length : 0
      return legendItemsLength
    })
    setSelectedDataPoints(legendItemsLength)
    setIsSelectionLimitExceeds(legendItemsLength > 20)
  }, [selectedLegends])

  const onZoneSelectionChanged = useCallback(
    (selectedZone, isChecked) => {
      const { zoneIndex, seriesName } = selectedZone
      const updatedZoneSeries = [...selectedLegends.zoneSeries]
      if (isChecked) {
        updatedZoneSeries[zoneIndex].series.push(seriesName)
      } else {
        const selectedIndex = updatedZoneSeries[zoneIndex].series.indexOf(seriesName)
        updatedZoneSeries[zoneIndex].series.splice(selectedIndex, 1)
      }
      setSelectedLegends({ ...selectedLegends, zoneSeries: updatedZoneSeries })

      const isSelected = updatedZoneSeries.filter(
        (item) => item.name === selectedZone.name
      )
      const gaAction = isSelected
        ? 'Select a legend item for the graph'
        : 'Deselect a legend item for the graph'

      GA4.sendEvent({
        category: 'Customer Detail Page',
        action: gaAction,
        label: selectedZone.name
      })
    },
    [selectedLegends]
  )

  const onFilterSelectionChanged = useCallback(
    (legendItem) => {
      const updatedSelectedLegends = selectedLegends.series.includes(legendItem)
        ? _.without(selectedLegends.series, legendItem)
        : _.union(selectedLegends.series, [legendItem])
      const gaAction = selectedLegends.series.includes(legendItem)
        ? 'Deselect a legend item for the graph'
        : 'Select a legend item for the graph'

      setSelectedLegends({
        ...selectedLegends,
        series: updatedSelectedLegends
      })

      GA4.sendEvent({
        category: 'Customer Detail Page',
        action: gaAction,
        label: legendItem
      })
    },
    [selectedLegends]
  )

  const onIndoorSelectionChanged = useCallback(
    (selectedLegend, isChecked) => {
      const updatedZoneSeries = _.map(selectedLegends.zoneSeries, (zone) => {
        const isSelectedZone = !_.isEmpty(zone.series)

        if (isChecked) {
          const noZonesSelected = selectedLegends.zoneSeries
            .map((zone) => _.isEmpty(zone.series))
            .every(Boolean)

          return {
            name: zone.name,
            series:
              isSelectedZone || noZonesSelected
                ? [...zone.series, selectedLegend]
                : zone.series
          }
        } else {
          return {
            name: zone.name,
            series: isSelectedZone
              ? zone.series.filter((item) => item !== selectedLegend)
              : zone.series
          }
        }
      })

      setSelectedLegends({ ...selectedLegends, zoneSeries: updatedZoneSeries })

      const isSelected = updatedZoneSeries
        ?.map((zone) => zone.series?.includes(selectedLegend))
        .includes(true)
      const gaAction = isSelected
        ? 'Select a legend item for the graph'
        : 'Deselect a legend item for the graph'
      GA4.sendEvent({
        category: 'Customer Detail Page',
        action: gaAction,
        label: selectedLegend
      })
    },
    [selectedLegends]
  )

  const onAlertsSelectionChanged = useCallback(
    (item) => {
      const updatedSelectedLegends = selectedLegends.alerts.includes(item)
        ? _.without(selectedLegends.alerts, item)
        : _.union(selectedLegends.alerts, [item])
      const gaAction = selectedLegends.alerts.includes(item)
        ? 'Deselect a legend item for the graph'
        : 'Select a legend item for the graph'

      setSelectedLegends({
        ...selectedLegends,
        alerts: updatedSelectedLegends
      })

      GA4.sendEvent({
        category: 'Customer Detail Page',
        action: gaAction,
        label: item
      })
    },
    [selectedLegends]
  )

  const handleShowTooltip = useCallback(() => {
    setDisplayTooltip(!displayTooltip)
    setSelectedLegends({ ...selectedLegends, showHoverData: !displayTooltip })
  }, [displayTooltip, selectedLegends])

  const onClickClearSelection = useCallback(() => {
    const updatedZoneSeries = selectedLegends.zoneSeries.map((zone) => {
      return { name: zone.name, series: [] }
    })
    setDisplayTooltip(true)

    setSelectedLegends({
      ...selectedLegends,
      series: [],
      zoneSeries: updatedZoneSeries,
      alerts: [],
      showHoverData: true
    })
  }, [selectedLegends])

  const onSaveFilter = useCallback(() => {
    GA4.sendEvent({
      category: 'Customer Detail Page',
      action: 'Save RTH Filter'
    })
    onSaveGraphFilterCallback(selectedLegends)
    closeCallback()
  }, [selectedLegends, onSaveGraphFilterCallback, closeCallback])

  return (
    <S.Modal
      variant="lg"
      isDisplayed
      closeCallback={closeCallback}
      fullWidth={false}
      title="Graph Filter"
    >
      <S.Container>
        <FilterList
          zones={zones}
          displayGroups={displayGroups}
          indoorLegendItems={indoorLegendItems}
          indoorUnitLegendItems={indoorUnitLegendItems}
          outdoorLegendItems={outdoorLegendItems}
          outdoorUnitLegendItems={outdoorUnitLegendItems}
          alerts={alerts}
          selectedLegends={selectedLegends}
          isControlHub={isControlHub}
          onZoneSelectionChanged={onZoneSelectionChanged}
          onFilterSelectionChanged={onFilterSelectionChanged}
          onIndoorSelectionChanged={onIndoorSelectionChanged}
          onAlertsSelectionChanged={onAlertsSelectionChanged}
          selectedDataPoints={selectedDataPoints}
          isHehm={isHehm}
        />

        <S.ButtonContainer>
          <Button
            decoration="secondary"
            onPress={onClickClearSelection}
            text="Clear selections"
          />
          {isSelectionLimitExceeds && (
            <S.Text>Only 20 data points allowed</S.Text>
          )}
          <S.Buttons>
            <Button
              decoration="tertiary"
              onPress={() => handleShowTooltip()}
            >
              {displayTooltip
                ? (<S.HoverButton><VisibilityOutlinedIcon />Show Hover Data in Graph</S.HoverButton>)
                : (<S.HoverButton><VisibilityOffOutlinedIcon />Hide Hover Data in Graph</S.HoverButton>)
              }
            </Button>

            <Button
              decoration="secondary"
              onPress={closeCallback}
              text="Cancel"
            />
            <Button
              decoration="positive"
              enabled={!isSelectionLimitExceeds}
              onPress={onSaveFilter}
              text="Apply"
            />
          </S.Buttons>
        </S.ButtonContainer>
      </S.Container>
    </S.Modal>
  )
}

export default GraphFilterModal

const S = {
  Modal: styled(Modal)`
    border-radius: 5px;
  `,
  Container: styled.div`
    background-color: ${color('paneBackground')};
    display: flex;
    flex-direction: column;
    padding: 10px;
  `,
  ButtonContainer: styled(ButtonContainer)`
    display: flex;
    margin: 10px;
    border-top: none;
    justify-content: space-between;
  `,
  Buttons: styled.div`
    display: flex;
  `,
  UserInfo: styled.div`
    display: flex;
  `,
  Text: styled.span`
    color: ${color('errorMessage')};
  `,
  HoverButton: styled.div`
    display: flex;
    gap: 5px;
    align-items: center;
  `
}
